import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { PaperPlaneTilt, Handshake, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import SwornFace from '../components/sworn/SwornFace';
import { Frame } from '../components/shell/AppShell';
import PageHeader from '../components/shell/PageHeader';
import { swornParent } from '../lib/navigation';
import swornArt from '../assets/bg/the-sworn.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POLL_MS = 10000;
const MAX_CHARS = 4000;

const when = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch (e) {
    return '';
  }
};

/**
 * The Sworn — the one room where members are named.
 *
 * Built after the Commons and deliberately unlike it in the places that
 * matter. Nobody can be silenced here, and nobody but the Founder can touch
 * another person's words.
 *
 * What a member CAN do is take back their own, which is not moderation — it
 * is the fix for having pasted something into the wrong window, and it has to
 * be immediate rather than a request to somebody else. The Founder can remove
 * anything, for the case where the author will not or cannot.
 *
 * That is an EXCEPTION to §1's "exactly one capability and nothing may creep
 * in beside it", and it is recorded as one rather than quietly added.
 *
 * A removal leaves a visible gap rather than vanishing. In a room of five
 * people who all read it, silence breeds more suspicion than the removal.
 *
 * Reaching this page without being in the circle is not an error state to
 * render. Every route answers 404 to an outsider, so there is nothing to show
 * and nothing to explain; the page simply sends them back. A screen saying
 * "you are not in the circle" would tell them the circle exists.
 */
const Sworn = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [members, setMembers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  const load = useCallback(async (opts = {}) => {
    try {
      const { data } = await axios.get(`${API_URL}/api/sworn/messages`, {
        withCredentials: true,
      });
      setMessages(data || []);
    } catch (e) {
      // A 404 here means the caller is not in the circle — including the case
      // where they were removed while the page was open. Leave rather than
      // explain.
      if (e.response?.status === 404) navigate('/', { replace: true });
      else if (!opts.quiet) setError('Could not open the room.');
    } finally {
      if (!opts.quiet) setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    axios.get(`${API_URL}/api/sworn/members`, { withCredentials: true })
      .then(({ data }) => setMembers(data || []))
      .catch(() => { /* the message load handles the 404 */ });
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => load({ quiet: true }), POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // One thread per pair, for good — so this is "go to", not "create". The
  // server returns the existing conversation when there is one, which is why
  // the same tap works the first time and every time after (§2.3).
  const openThread = async (memberId) => {
    if (opening) return;
    setOpening(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/sworn/threads/${memberId}`, {}, { withCredentials: true },
      );
      navigate(`/sworn/thread/${data.id}`);
    } catch (e) {
      if (e.response?.status === 404) navigate('/', { replace: true });
      else setError(e.response?.data?.detail || 'That conversation could not be opened.');
    } finally {
      setOpening(false);
    }
  };

  const remove = async (id, wasMine) => {
    // No confirmation dialogue. The room is small and the usual reason for
    // pressing this is that something is on screen which should not be —
    // a client's details, the wrong window. Asking "are you sure?" while it
    // is still visible gets the order the wrong way round.
    try {
      await axios.delete(`${API_URL}/api/sworn/messages/${id}`, { withCredentials: true });
      // Matches what the server will send on the next poll: your own
      // withdrawal leaves nothing, somebody else's removal leaves the mark.
      setMessages((prev) => (wasMine
        ? prev.filter((m) => m.id !== id)
        : prev.map((m) => (m.id === id ? { ...m, is_deleted: true, content: null } : m))));
    } catch (err) {
      setError('That could not be removed.');
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/sworn/messages`, { content }, { withCredentials: true },
      );
      setMessages((prev) => [...prev, data]);
      setDraft('');
    } catch (err) {
      if (err.response?.status === 404) navigate('/', { replace: true });
      else setError(err.response?.data?.detail || 'That did not send.');
    } finally {
      setSending(false);
    }
  };

  const parent = swornParent(user);

  return (
    <Frame user={user} background={swornArt} scrim={0.45} activeKey={parent.key} fill>
    <div className="flex-1 flex flex-col min-h-0" data-testid="sworn-page">
      {/* Says the thing a member most needs to remember about this room, and it
          is the opposite of what the Commons says. Somebody who uses both
          should never be unsure which one they are in. */}
      <PageHeader
        title="The Sworn"
        subtitle="Here you are known by name"
        onBack={() => navigate(parent.to)}
      >
        {members.length > 0 && (
          // Centred while it fits, scrollable from the first name when it
          // does not: centring a scrolling row directly would push the start
          // of it off-screen where no scroll can reach.
          <div className="mt-3 overflow-x-auto pb-1" data-testid="sworn-members">
          <div className="mx-auto flex w-max items-center gap-2 px-1">
            {members.map((m) => {
              const isMe = m.id === user?.id;
              const chip = (
                <>
                  <SwornFace display={m.display} />
                  <span className="text-xs text-foreground/90 whitespace-nowrap">
                    {isMe ? 'You' : m.display}
                  </span>
                </>
              );
              // THE ONE DOOR. Tapping a member opens the private thread with
              // them, and this is the only way into that feature anywhere in
              // Arcanum -- no member search, no "message this member" in the
              // Commons, no addressing anybody by number. A capability with
              // one door, inside a room only the circle can see, is what keeps
              // "the Sworn is a room, not a capability" true (§2.1, §2.3).
              return isMe ? (
                <div key={m.id} className="flex items-center gap-1.5 shrink-0 rounded-full border border-border bg-card/60 py-0.5 pl-0.5 pr-2.5">{chip}</div>
              ) : (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => openThread(m.id)}
                  disabled={opening}
                  className="flex items-center gap-1.5 shrink-0 rounded-full border border-primary/40 bg-card/70 py-0.5 pl-0.5 pr-2.5 hover:bg-primary/10 transition-colors disabled:opacity-60"
                  aria-label={`Write privately to ${m.display}`}
                  data-testid={`sworn-open-thread-${m.id}`}
                >
                  {chip}
                </button>
              );
            })}
          </div>
          </div>
        )}
        {members.length > 1 && (
          <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="sworn-door-hint">
            Tap a name to speak privately.
          </p>
        )}
      </PageHeader>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 py-3" data-testid="sworn-messages">
        {loading ? (
          <p className="text-sm text-muted-foreground text-center py-10" role="status">Opening the room…</p>
        ) : messages.length === 0 ? (
          <div className="text-center py-16">
            <Handshake className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Nobody has spoken yet. Someone has to be first.
            </p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto space-y-3">
            {messages.map((m) => {
              const mine = m.sender?.id && m.sender.id === user?.id;
              const departed = !m.sender;
              // The Founder may remove anything; everybody else, only their
              // own. Not shown at all where it is not permitted -- a control
              // that refuses is worse than one that is absent.
              const mayRemove = !m.is_deleted && (mine || user?.role === 'superadmin');
              return (
                <div
                  key={m.id}
                  className={`flex items-start gap-2.5 ${mine ? 'flex-row-reverse' : ''}`}
                  data-testid={`sworn-message-${m.id}`}
                >
                  <SwornFace display={m.sender_display} muted={departed} />
                  <div
                    className={[
                      'min-w-0 max-w-[82%] rounded-xl border px-3 py-2 backdrop-blur-sm bg-card/90',
                      mine ? 'border-primary/40' : 'border-border',
                    ].join(' ')}
                    style={mine ? { backgroundImage: 'linear-gradient(hsl(var(--gold) / 0.10), hsl(var(--gold) / 0.10))' } : undefined}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className={`text-xs font-semibold ${departed ? 'italic text-muted-foreground' : ''}`}
                        style={{ color: mine ? 'hsl(var(--gold))' : departed ? undefined : 'hsl(var(--gold-pale))' }}
                      >
                        {mine ? 'You' : m.sender_display}
                      </span>
                      {mayRemove && (
                        <button
                          type="button"
                          onClick={() => remove(m.id, Boolean(mine))}
                          className="ml-auto -my-2 -mr-2 flex h-10 w-10 items-center justify-center text-muted-foreground/70 hover:text-destructive"
                          aria-label={mine ? 'Remove what you wrote' : 'Remove this message'}
                          data-testid={`sworn-remove-${m.id}`}
                        >
                          <Trash className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {/* anywhere, not break-word: only anywhere shrinks the
                        intrinsic width, which is what stops a long unbroken
                        run widening the row past the screen. */}
                    {m.is_deleted ? (
                      <p className="text-sm italic text-muted-foreground mt-0.5">
                        This message was removed
                      </p>
                    ) : (
                      <p
                        className="text-sm leading-relaxed whitespace-pre-wrap mt-0.5 text-foreground"
                        style={{ overflowWrap: 'anywhere' }}
                      >
                        {m.content}
                      </p>
                    )}
                    <p className={`mt-1 text-[10px] text-muted-foreground ${mine ? 'text-right' : ''}`}>
                      {when(m.created_at)}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
        )}
      </div>

      <form onSubmit={send} className="border-t border-border bg-card/90 p-3" data-testid="sworn-composer">
        <div className="max-w-2xl mx-auto">
          {error && <p role="alert" className="text-sm text-destructive mb-2" data-testid="sworn-error">{error}</p>}
          <div className="flex items-end gap-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send(e);
                }
              }}
              placeholder="Say something to the circle…"
              aria-label="Say something to the circle"
              rows={1}
              maxLength={MAX_CHARS}
              className="min-h-[44px] max-h-40 resize-none"
              data-testid="sworn-input"
            />
            <Button
              type="submit"
              disabled={sending || !draft.trim()}
              className="h-11 w-11 shrink-0 bg-primary hover:bg-primary/90"
              aria-label="Send"
              data-testid="sworn-send"
            >
              <PaperPlaneTilt className="w-4 h-4" weight="fill" />
            </Button>
          </div>
        </div>
      </form>
    </div>
    </Frame>
  );
};

export default Sworn;
